'use client';

import { useTranslations } from 'next-intl';
import { Component } from '@headless-workspace/typings';
import { VipBrandsValue } from '../../../../server';
import { useAppLocale } from '../../i18n';
import { SearchSuggestions } from './SearchSuggestions';
import { SearchSuggestionsError } from './SearchSuggestionsError';
import { SearchSuggestionsSkeleton } from './SearchSuggestionsSkeleton';
import { Suggest } from './Suggest';
import { useSearchSuggestions } from './useSearchSuggestions';

type SearchSuggestionsContainerProps = {
    searchInput: string;
    onSearch: (search: string) => void;
    vipBrands: VipBrandsValue;
};

export const SearchSuggestionsContainer: Component<SearchSuggestionsContainerProps> = ({
    searchInput,
    onSearch,
    vipBrands,
}) => {
    const t = useTranslations('Header.search');
    const locale = useAppLocale();

    // Retrieve search results
    const {
        isLoading,
        result: searchSuggestionsResult,
        shouldDisplayTags,
    } = useSearchSuggestions(searchInput, vipBrands);

    const onSearchHandler = () => {
        onSearch(searchInput);
    };

    if (isLoading) {
        return <SearchSuggestionsSkeleton onSearchHandler={onSearchHandler} />;
    }

    if (searchSuggestionsResult.type !== 'success') {
        return <SearchSuggestionsError searchInput={searchInput} />;
    }

    return (
        <>
            <SearchSuggestions
                suggestedProducts={searchSuggestionsResult.data.products}
                onSearchHandler={onSearchHandler}
                locale={locale}
            />
            {shouldDisplayTags && (
                <section className={'flex flex-col gap-1.5 flex-1'}>
                    <Suggest
                        title={t('label.suggestions')}
                        category={'suggestion'}
                        suggestions={searchSuggestionsResult.data.terms}
                        locale={locale}
                    />
                    <Suggest
                        title={t('label.categories')}
                        category={'category'}
                        suggestions={searchSuggestionsResult.data.categories}
                        locale={locale}
                    />
                    <Suggest
                        title={t('label.brands')}
                        category={'brand'}
                        suggestions={searchSuggestionsResult.data.brands}
                        locale={locale}
                    />
                </section>
            )}
        </>
    );
};
